import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, FileText, Eye } from "lucide-react";

const SECTION_TITLE = {
  acute: "Acute Care", primary: "Primary Care", chronic: "Chronic Care",
  expert: "Expert Opinions", mental: "Mental Health",
};

function DocViewer({ provider, onClose }) {
  const [full, setFull] = useState(provider);
  useEffect(() => {
    api.get(`/enterprise/providers/${provider.id}`).then(({ data }) => setFull(data.provider)).catch(() => {});
  }, [provider.id]);
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent data-testid="doc-viewer" className="max-w-lg rounded-3xl">
        <h3 className="font-head text-xl font-semibold text-[#041E26] mb-1">{full.name}</h3>
        <p className="text-sm text-[#D97706] mb-1">{full.specialty} · {SECTION_TITLE[full.section]}</p>
        <span className="inline-block text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 mb-4">{full.provider_type === "therapist" ? "Mental Health Professional" : "Medical Doctor"}</span>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-400 text-xs">Aadhaar</span><div className="font-medium">{full.aadhaar_number || "—"}</div></div>
          {full.provider_type === "therapist" ? (
            <div><span className="text-slate-400 text-xs">License ({full.license_authority || "RCI"})</span><div className="font-medium">{full.license_number || "—"}</div></div>
          ) : (
            <div><span className="text-slate-400 text-xs">Registration</span><div className="font-medium">{full.registration_number || "—"}</div></div>
          )}
          <div className="col-span-2"><span className="text-slate-400 text-xs">Education</span><div className="font-medium">{full.education || "—"}</div></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {[["Aadhaar card", full.aadhaar_doc], ["Certificate", full.certificate_doc]].map(([label, doc]) => (
            <div key={label} className="border border-slate-200 rounded-xl p-3">
              <div className="text-xs text-slate-500 mb-2">{label}</div>
              {doc ? (doc.startsWith("data:image") ? <img src={doc} alt={label} className="w-full h-32 object-cover rounded-lg" /> : <a href={doc} target="_blank" rel="noreferrer" className="text-[#D97706] text-sm flex items-center gap-1"><FileText className="w-4 h-4" /> View document</a>) : <span className="text-slate-300 text-sm">Not uploaded</span>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EnterprisePortal() {
  const [providers, setProviders] = useState([]);
  const [appts, setAppts] = useState([]);
  const [view, setView] = useState(null);

  const load = async () => {
    const [p, a] = await Promise.all([api.get("/enterprise/providers"), api.get("/appointments")]);
    setProviders(p.data.providers); setAppts(a.data.appointments);
  };
  useEffect(() => { load(); }, []);

  const setVerified = async (id, verify) => {
    try { await api.post(`/enterprise/providers/${id}/${verify ? "verify" : "revoke"}`); toast.success(verify ? "Provider verified" : "Verification revoked"); load(); }
    catch { toast.error("Action failed"); }
  };

  const pending = providers.filter((p) => !p.verified);

  return (
    <div>
      <section className="mb-12">
        <h2 className="font-head text-2xl font-semibold text-[#041E26] mb-1">Provider verifications</h2>
        <p className="text-slate-500 mb-5">Review credentials and approve providers so they appear to patients. {pending.length > 0 && <span className="text-[#D97706] font-semibold">{pending.length} pending.</span>}</p>
        {providers.length === 0 ? <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No provider submissions yet.</div> : (
          <div className="space-y-3">
            {providers.map((p) => (
              <div key={p.id} data-testid={`prov-row-${p.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <img src={p.photo || "https://ui-avatars.com/api/?name=" + encodeURIComponent(p.name)} alt="" className="w-12 h-12 rounded-full object-cover bg-slate-100" />
                  <div>
                    <div className="font-semibold text-[#041E26]">{p.name}</div>
                    <div className="text-sm text-slate-500">{p.specialty} · {SECTION_TITLE[p.section] || p.section} · {p.qualification}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button data-testid={`view-docs-${p.id}`} variant="outline" onClick={() => setView(p)} className="rounded-full gap-2"><Eye className="w-4 h-4" /> Docs</Button>
                  {p.verified ? (
                    <>
                      <Badge className="bg-green-100 text-green-700 gap-1"><ShieldCheck className="w-3.5 h-3.5" /> verified</Badge>
                      <Button data-testid={`revoke-${p.id}`} variant="outline" onClick={() => setVerified(p.id, false)} className="rounded-full gap-2 text-red-500 border-red-200 hover:bg-red-50"><ShieldX className="w-4 h-4" /> Revoke</Button>
                    </>
                  ) : (
                    <Button data-testid={`verify-${p.id}`} onClick={() => setVerified(p.id, true)} className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-semibold gap-2"><ShieldCheck className="w-4 h-4" /> Verify</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-head text-2xl font-semibold text-[#041E26] mb-4">Network appointments</h2>
        {appts.length === 0 ? <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No appointments across the network yet.</div> : (
          <div className="space-y-3">
            {appts.slice(0, 25).map((a) => (
              <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between text-sm">
                <span className="font-medium text-[#041E26]">{a.patient_name} → {a.doctor_name}</span>
                <span className="text-slate-400">{a.doctor_specialty}</span>
                <Badge className="bg-slate-100 text-slate-600">{a.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </section>

      {view && <DocViewer provider={view} onClose={() => setView(null)} />}
    </div>
  );
}
